<script>
import {
  detectTransportSupport,
  TRANSPORT_ACCESS_POINT_ONLY,
  TRANSPORT_SWITCH_BROWSER,
  AP_SSID_PREFIX,
  AP_DEFAULT_PASSWORD,
  AP_ADDRESS,
} from '../js/transportSupport.js';

/**
 * Tells someone their browser cannot reach the device, and what to do (MDL-392).
 *
 * A new customer is sent here, and on an iPhone nothing works: Web Serial is
 * Chromium-desktop only, and reaching the device over HTTP from this HTTPS
 * page needs Private Network Access, which only Chromium implements. Every
 * browser on iOS is WebKit, so there is no browser they can switch to — the
 * access point is the only route, and it works because the device serves its
 * own editor over plain HTTP.
 *
 * On a desktop the advice differs, because switching browser restores both
 * transports and is better than joining an access point.
 *
 * This replaces the "Connect device" hero rather than sitting above it as a
 * banner. Told to connect a device they cannot connect, someone will go
 * looking for a cable — the instruction has to be the one that works, not a
 * caveat beside one that does not.
 */
export default {
  name: 'TransportGuidance',

  props: {
    // Injectable so each branch is testable from a snapshot rather than
    // whichever browser the tests happen to run in.
    navigatorRef: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      ssidPrefix: AP_SSID_PREFIX,
      apPassword: AP_DEFAULT_PASSWORD,
      apAddress: AP_ADDRESS,
    };
  },

  computed: {
    support() {
      return detectTransportSupport(this.navigatorRef ?? globalThis.navigator);
    },

    showAccessPoint() {
      return this.support.advice === TRANSPORT_ACCESS_POINT_ONLY;
    },

    showSwitchBrowser() {
      return this.support.advice === TRANSPORT_SWITCH_BROWSER;
    },

    visible() {
      return this.showAccessPoint || this.showSwitchBrowser;
    },
  },
};
</script>

<template>
  <div v-if="visible" class="hero" data-testid="transport-guidance" :data-advice="support.advice">
    <div class="hero-content text-center">
      <div class="max-w-lg">
        <!-- iOS: there is no browser to switch to, so the access point is the
             instruction, not a fallback. -->
        <template v-if="showAccessPoint">
          <h2 class="text-4xl font-bold">Use your pedal's own editor</h2>
          <p class="py-4">
            iPhones and iPads can't connect to Midulate over USB or over your home
            WiFi — no browser on iOS can. Your pedal has a built-in editor with
            everything you need, and you reach it by joining the pedal's own
            network.
          </p>

          <!-- No "switch on broadcast mode" step: it is on by default on a new
               pedal (wifi_ap_disabled is false in the factory config), so the
               instruction only sends someone hunting for a setting they do not
               need to touch. -->
          <ol class="text-left space-y-3 mt-2">
            <li>
              <span class="font-semibold">1.</span>
              In your WiFi settings, join the WiFi network starting
              <span class="font-mono font-semibold">{{ ssidPrefix }}</span> —
              the last four characters are unique to your pedal.
            </li>
            <li>
              <span class="font-semibold">2.</span>
              If it asks for a password, a new pedal uses
              <code class="font-mono">{{ apPassword }}</code>.
            </li>
            <li>
              <span class="font-semibold">3.</span>
              Open
              <a class="link link-primary font-mono" :href="apAddress">{{ apAddress }}</a>.
            </li>
          </ol>
        </template>

        <!-- Desktop Safari or Firefox: switching restores USB and WiFi both,
             which beats sending them to the access point. -->
        <template v-else-if="showSwitchBrowser">
          <h2 class="text-4xl font-bold">This browser can't connect</h2>
          <p class="py-4">
            Connecting to Midulate over USB or WiFi needs Chrome or Edge. Open
            this page in one of those and everything will work as normal.
          </p>
          <div class="divider text-sm">or</div>
          <!-- Same size as the Chrome/Edge paragraph above: these are two
               equally valid routes, and setting one smaller reads as a footnote
               rather than an alternative. -->
          <p class="py-4">
            Use the pedal's built-in editor instead. Join the WiFi network
            starting <span class="font-mono font-semibold">{{ ssidPrefix }}</span> —
            the last four characters are unique to your pedal. If it asks for a
            password, a new pedal uses
            <code class="font-mono">{{ apPassword }}</code>. Then open
            <a class="link link-primary font-mono" :href="apAddress">{{ apAddress }}</a>.
          </p>
        </template>
      </div>
    </div>
  </div>
</template>
